import { Link } from "react-router-dom";
import { HelpCircle, Save, CalendarClock, ArrowRight } from "lucide-react";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

/**
 * Section "Avec ou sans compte" de la landing (S16-3).
 *
 * Assume le **double parcours** de la V1 (décision PO 2026-06-12) :
 * - parcours **anonyme** rapide pour *tester* sans friction ;
 * - **compte** pour *sauvegarder durablement* ses simulations et gérer
 *   plusieurs sites (« Mes sites »).
 *
 * Inclut le **teaser Enedis roadmap** — wording prudent **obligatoire**
 * (relecture security-rgpd) : la connexion Enedis **n'est pas** disponible
 * et **n'est pas** promise comme automatique/live (convention partenaire R-8
 * non signée, cf. sprint-15 § S15-5). On annonce uniquement, et explicitement
 * « à venir », une analyse longue durée à partir de données Enedis réelles.
 * L'import CSV/export portail reste, lui, déjà disponible et honnête.
 *
 * Vocabulaire de nav figé S16-2 : « Nouvelle simulation » (`/simulations/new`),
 * « Créer un compte » (`/signup`).
 *
 * Responsive : 1 col mobile, 2 cols à partir de `md` (S16-1).
 */
export function LandingAccounts(): JSX.Element {
  return (
    <section
      aria-labelledby="accounts-title"
      className="bg-white py-16 sm:py-20"
    >
      <div className="mx-auto max-w-6xl px-4">
        <div className="mx-auto max-w-2xl text-center">
          <h2
            id="accounts-title"
            className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl"
          >
            Testez librement, sauvegardez si vous voulez
          </h2>
          <p className="mt-4 text-base text-slate-700">
            Lancez une première simulation sans rien créer. Ouvrez un compte
            seulement quand vous souhaitez retrouver vos résultats et comparer
            plusieurs logements.
          </p>
        </div>

        <div className="mt-12 grid gap-6 md:grid-cols-2">
          <Card className="flex h-full flex-col gap-4 p-6">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
              <HelpCircle className="h-5 w-5" aria-hidden />
            </div>
            <h3 className="text-lg font-semibold text-slate-900">
              Sans compte, pour tester
            </h3>
            <p className="text-sm leading-relaxed text-slate-700">
              Aucun email, aucun mot de passe ! Une session anonyme suffit pour
              lancer une simulation complète.
            </p>
            <div className="mt-auto pt-2">
              <Button asChild variant="outline" className="w-full sm:w-auto">
                <Link
                  to="/simulations/new"
                  className="inline-flex items-center justify-center gap-2"
                >
                  Nouvelle simulation
                  <ArrowRight className="h-4 w-4" aria-hidden />
                </Link>
              </Button>
            </div>
          </Card>

          <Card className="flex h-full flex-col gap-4 border-amber-200 bg-amber-50/40 p-6">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-amber-700 text-white">
              <Save className="h-5 w-5" aria-hidden />
            </div>
            <h3 className="text-lg font-semibold text-slate-900">
              Avec un compte, pour aller plus loin
            </h3>
            <p className="text-sm leading-relaxed text-slate-700">
              Sauvegardez durablement vos simulations, gérez plusieurs logements,
              et analysez vos scénarios quand vous voulez.
            </p>
            <div className="mt-auto pt-2">
              <Button asChild className="w-full sm:w-auto">
                <Link
                  to="/signup"
                  className="inline-flex items-center justify-center gap-2"
                >
                  Créer un compte
                  <ArrowRight className="h-4 w-4" aria-hidden />
                </Link>
              </Button>
            </div>
          </Card>
        </div>

        {/* Teaser Enedis ROADMAP — wording prudent obligatoire (S16-3).
            Aucune promesse de connexion automatique/live : R-8 non signée
            (sprint-15 § S15-5). À relire par security-rgpd avant commit. */}
        <div className="mt-6 flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50 p-6 sm:flex-row sm:items-center sm:gap-4">
          <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-200 text-slate-700">
            <CalendarClock className="h-5 w-5" aria-hidden />
          </div>
          <div className="space-y-1">
            <p className="flex flex-wrap items-center gap-2 text-base font-semibold text-slate-900">
              Bientôt&nbsp;: l'analyse longue durée à partir de vos données
              Enedis réelles
              <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs font-medium uppercase tracking-wide text-slate-700">
                À venir
              </span>
            </p>
            <p className="text-sm leading-relaxed text-slate-700">
              Aujourd'hui, vous importez vous-même votre courbe de charge
              (un export Enedis depuis votre espace client). Une analyse sur
              plusieurs mois à partir de vos données Enedis réelles est à l'étude
              pour une prochaine version.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
